/** Meses de cada ciclo recorrente. `pagamento_unico` não se repete. */
const CYCLE_MONTHS: Record<string, number> = {
  mensal: 1,
  trimestral: 3,
  semestral: 6,
  anual: 12,
};

/** Meses de um ciclo (mensal=1, trimestral=3, semestral=6, anual=12); null se não recorrente. */
export function billingCycleMonths(cycle: string): number | null {
  return CYCLE_MONTHS[cycle] ?? null;
}

const YMD = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Soma meses de CALENDÁRIO a um dia 'YYYY-MM-DD' (aceita ISO com hora; só o dia
 * conta). `day` fixa o dia do mês de destino (ex: o dia de cobrança combinado);
 * sem ele, mantém o dia de `ymd`. Dia que não existe no mês de destino vira o
 * último dia dele (31/01 + 1 mês = 28/02 ou 29/02) — nunca "pula" pro mês
 * seguinte como o `setMonth` do JS faria.
 *
 * Base da política "mesmo dia todo mês" (DR-020 §1.5, revisada 2026-09-29):
 * antes o mensal somava 30 dias corridos e o dia combinado ia escorregando.
 */
export function addCalendarMonths(ymd: string, months: number, day?: number): string {
  const match = YMD.exec(ymd);
  if (!match) {
    throw new Error(`addCalendarMonths: data inválida "${ymd}" (esperado YYYY-MM-DD).`);
  }
  const [, y, m, d] = match.map(Number) as [number, number, number, number];
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const lastDayOfTarget = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  const wantedDay = Math.min(day ?? d, lastDayOfTarget);
  const yy = String(target.getUTCFullYear()).padStart(4, '0');
  const mm = String(target.getUTCMonth() + 1).padStart(2, '0');
  return `${yy}-${mm}-${String(wantedDay).padStart(2, '0')}`;
}

/**
 * Fim de uma assinatura de PERÍODO FECHADO (criada sem renovação automática):
 * mesmo dia, um ciclo depois do início.
 *
 * Caso real 2026-09-29 (Gisele): Pix único, assinatura mensal sem renovação
 * era pra acabar sozinha em 30 dias e ficou ativa pra sempre. Regra única:
 * a API grava esta data e a tela de criação mostra a mesma.
 *
 * @returns 'YYYY-MM-DD' do fim, ou null quando o ciclo não tem fim por período
 */
export function closedPeriodEnd(startYMD: string, cycle: string): string | null {
  const months = billingCycleMonths(cycle);
  if (!months) return null;
  if (!YMD.test(startYMD)) {
    throw new Error(`closedPeriodEnd: data inválida "${startYMD}" (esperado YYYY-MM-DD).`);
  }
  return addCalendarMonths(startYMD, months);
}
